import maplibregl, { AddLayerObject, MapLayerMouseEvent } from 'maplibre-gl';
import { useCallback, useEffect } from 'react';
import { LayerSpecification, MapGeoJSONFeature } from 'react-map-gl/maplibre';

import { useToolTipContext } from '@/providers/ToolTipProvider';

import { capitalizeEveryWord } from '../../utils/common';
import { isDefined } from '../../utils/guards';

interface IOptions {
  map: maplibregl.Map | undefined;
  shouldDisplayToolTip: boolean;
  hoveredLayer?: LayerSpecification | AddLayerObject;
  propertyKey?: string;
  externalData?: null | any;
  formatData?: Function;
  formatText?: (text: string) => string;
  truncate?: boolean;
}

/**
 * Controls the hover of a feature on a specific layer and displays a tooltip
 * @param map
 * @param shouldDisplayToolTip
 * @param hoveredLayer The layer that the mouse is over
 * @param propertyKey The key from the properties that will be displayed on hover
 */
const useLayerToolTipHover = (options: IOptions) => {
  const {
    map,
    shouldDisplayToolTip,
    hoveredLayer,
    propertyKey,
    externalData,
    formatData,
    formatText,
    truncate = false,
  } = { externalData: null, ...options };
  const { clearToolTip, setIsVisible, setText } = useToolTipContext();

  const onMouseEvent = useCallback(
    (e: MapLayerMouseEvent) => {
      if (!map || !hoveredLayer?.id || !isDefined(propertyKey)) return;
      if (externalData === undefined) return;

      const renderedTiles = map.queryRenderedFeatures(e.point, {
        layers: [hoveredLayer.id],
      });
      const [first]: MapGeoJSONFeature[] = renderedTiles;

      if (!shouldDisplayToolTip) return;

      if (
        !(first && first.properties?.[propertyKey] && e.type !== 'mouseleave')
      ) {
        clearToolTip();
        setIsVisible(false);

        return;
      }

      if (externalData && formatData) {
        const data = externalData[first.properties?.[propertyKey]];
        const formattedData = formatData({ ...first.properties, ...data });
        if (formattedData) {
          setText(formattedData);
          setIsVisible(true);
        } else {
          clearToolTip();
        }
      } else {
        const text: string = first.properties?.[propertyKey] || '';
        const truncatedText =
          truncate && text.length > 20 ? `${text.slice(0, 20)}...` : text;

        const formattedText = formatText
          ? formatText(truncatedText)
          : capitalizeEveryWord(truncatedText);

        setText(formattedText);
        setIsVisible(true);
      }
    },
    [
      map,
      hoveredLayer?.id,
      propertyKey,
      externalData,
      shouldDisplayToolTip,
      formatData,
      truncate,
      formatText,
    ]
  );

  /** Clean up tooltip when unmounting */
  useEffect(() => {
    return () => {
      clearToolTip();
      setIsVisible(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Add MapBox listeners and clear MapBox listeners and tooltip here
  useEffect(() => {
    if (!map || !hoveredLayer?.id) return () => {};

    if (!shouldDisplayToolTip) {
      clearToolTip();
    }

    map.on('mousemove', hoveredLayer.id, onMouseEvent);
    map.on('mouseenter', hoveredLayer.id, onMouseEvent);
    map.on('mouseleave', hoveredLayer.id, onMouseEvent);

    return () => {
      map.off('mousemove', hoveredLayer.id, onMouseEvent);
      map.off('mouseenter', hoveredLayer.id, onMouseEvent);
      map.off('mouseleave', hoveredLayer.id, onMouseEvent);
    };
  }, [hoveredLayer?.id, map, onMouseEvent, shouldDisplayToolTip]);
};

export default useLayerToolTipHover;
